import {
  Bearing,
  BearingAnchor,
  Connector,
  ConnectorType,
  LineLocation,
  RelativeBearingGeometry,
  RelativeBearingsGeometryBase,
  RelativeContinuousBearingGeometry,
} from '@paragon/contract-library';
import { distanceGreaterThanOrEqual, distanceLessThanOrEqual } from '@paragon/geometry-library';
import { enqueueError } from '@paragon/notification-tools';
import { findAndCalculateLine, findLine, isExplicitCalculatedLine } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import pushUniqByGuid from '../../library/utilities/pushUniqByGuid';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getPickingObjectInfo } from '../selectors/pickingSelectors';
import { clearPickingObject } from '../slices/applicationSlice';
import { setBearing, setReferenceLine } from '../slices/componentDesignsSlice';
import { ThunkActionVoid } from './thunkActions';

/**
 * Set the bearing on the component design.
 */
export const setBearingOnComponentDesign =
  (bearing: Bearing, componentDesignGuid: string): ThunkActionVoid =>
  (dispatch) =>
    dispatch(setBearing({ componentDesignGuid, bearing }));

export const setBearingWidthAbsoluteGeometry =
  (bearing: Bearing, componentDesignGuid: string, newWidth: number): ThunkActionVoid =>
  (dispatch) => {
    if (distanceGreaterThanOrEqual(0, newWidth)) return;
    if (bearing.relativeGeometry) {
      console.error('Expected absolute geometry.');
      return;
    }
    const positionShiftMultiple = getMultipleToShiftPositionBy(bearing.anchor);
    const xPositionShift = (bearing.width - newWidth) * positionShiftMultiple;
    const newBearing = { ...bearing, width: newWidth, centerPointX: bearing.centerPointX + xPositionShift };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

const getMultipleToShiftPositionBy = (bearingAnchor: BearingAnchor) => {
  switch (bearingAnchor) {
    case 'Center':
      return 0;
    case 'Left':
      return -0.5;
    case 'Right':
      return 0.5;
    default:
      assertExhaustiveSwitch(bearingAnchor, 'Unknown bearing anchor');
  }
};

export const setBearingWidth =
  (bearing: Bearing, componentDesignGuid: string, width: number): ThunkActionVoid =>
  (dispatch) => {
    if (distanceLessThanOrEqual(width, 0)) return;

    if (!bearing.relativeGeometry) {
      dispatch(setBearingWidthAbsoluteGeometry(bearing, componentDesignGuid, width));
    } else if (bearing.relativeGeometry.bearingGeometryType === 'Bearing') {
      dispatch(setBearingWidthRelativeGeometry(bearing, componentDesignGuid, width));
    }
  };

export const shiftBearingPosition =
  (bearing: Bearing, componentDesignGuid: string, xChange: number): ThunkActionVoid =>
  (dispatch) => {
    if (!bearing.relativeGeometry) {
      dispatch(shiftBearingPositionAbsoluteGeometry(bearing, componentDesignGuid, xChange));
    } else if (bearing.relativeGeometry.bearingGeometryType === 'Bearing') {
      dispatch(shiftBearingPositionRelativeGeometry(bearing, componentDesignGuid, xChange));
    }
  };

export const shiftBearingPositionAbsoluteGeometry =
  (bearing: Bearing, componentDesignGuid: string, xChange: number): ThunkActionVoid =>
  (dispatch) => {
    if (bearing.relativeGeometry) {
      console.error('Expected absolute geometry.');
      return;
    }
    const newBearing = { ...bearing, centerPointX: bearing.centerPointX + xChange };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

const shiftBearingPositionRelativeGeometry =
  (bearing: Bearing, componentDesignGuid: string, xChange: number): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesign = getActiveComponentDesign(getState());
    if (!componentDesign || componentDesign.guid !== componentDesignGuid) {
      console.error('Expect component design to match active component design.');
      return;
    }
    const { relativeGeometry } = bearing;
    if (relativeGeometry?.bearingGeometryType !== 'Bearing') {
      console.error('Expected bearing geometry type to be Bearing');
      return;
    }
    const definitionLine = findLine(relativeGeometry.lineGuid, componentDesign.component);
    if (definitionLine?.lineType !== 'Parallel') {
      console.error('Expected bearing definition line to be a parallel line.');
      return;
    }
    const newDefinitionLine = { ...definitionLine, offset: definitionLine.offset + xChange };
    dispatch(setReferenceLine({ componentDesignGuid, referenceLine: newDefinitionLine }));
  };

export const changeBearingAnchorAbsoluteGeometry =
  (bearing: Bearing, componentDesignGuid: string, newAnchor: BearingAnchor): ThunkActionVoid =>
  (dispatch) => {
    const newBearing = { ...bearing, anchor: newAnchor };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const setBearingWidthRelativeGeometry =
  (bearing: Bearing, componentDesignGuid: string, newWidth: number): ThunkActionVoid =>
  (dispatch) => {
    if (distanceGreaterThanOrEqual(0, newWidth)) return;
    if (!bearing.relativeGeometry) {
      console.error('Bearing did not have relative geometry.');
      return;
    }
    const { relativeGeometry } = bearing;
    const newRelativeGeometry = { ...relativeGeometry, width: newWidth };
    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const setBearingLineLocation =
  (bearing: Bearing, componentDesignGuid: string, lineLocation: LineLocation): ThunkActionVoid =>
  (dispatch) => {
    const { relativeGeometry } = bearing;
    if (!relativeGeometry || relativeGeometry.bearingGeometryType !== 'Bearing') {
      console.error('Bearing did not have relative geometry.');
      return;
    }
    const newRelativeGeometry = { ...relativeGeometry, lineLocation };
    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const setBearingReverseDirection =
  (bearing: Bearing, componentDesignGuid: string, directionReversed: boolean): ThunkActionVoid =>
  (dispatch) => {
    const { relativeGeometry } = bearing;
    if (!relativeGeometry) {
      console.error('Bearing did not have relative geometry.');
      return;
    }
    const newRelativeGeometry = { ...relativeGeometry, directionReversed };
    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const setBearingLineRegularBearing =
  (
    bearing: Bearing,
    componentDesignGuid: string,
    bearingLineProperty: 'bearingLineGuid' | 'lineGuid',
    lineGuid: string,
  ): ThunkActionVoid =>
  (dispatch) => {
    if (!bearing.relativeGeometry || bearing.relativeGeometry.bearingGeometryType !== 'Bearing') {
      console.error('Bearing does not have correct relative geometry.');
      return;
    }

    if (bearing.relativeGeometry[bearingLineProperty] === lineGuid) {
      return;
    }

    const newRelativeGeometry = { ...bearing.relativeGeometry, [bearingLineProperty]: lineGuid };
    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const setBearingLineContinuousBearing =
  (
    bearing: Bearing,
    componentDesignGuid: string,
    bearingLineProperty: 'bearingLineGuid' | 'leftSideLineGuid' | 'rightSideLineGuid',
    lineGuid: string,
  ): ThunkActionVoid =>
  (dispatch) => {
    if (!bearing.relativeGeometry || bearing.relativeGeometry.bearingGeometryType !== 'ContinuousBearing') {
      console.error('Bearing does not have correct relative geometry.');
      return;
    }

    if (bearing.relativeGeometry[bearingLineProperty] === lineGuid) {
      return;
    }

    const newRelativeGeometry = { ...bearing.relativeGeometry, [bearingLineProperty]: lineGuid };
    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const switchBearingRelativeGeometryType =
  (
    bearing: Bearing,
    componentDesignGuid: string,
    newBearingGeometryType: 'Bearing' | 'ContinuousBearing',
  ): ThunkActionVoid =>
  (dispatch, getState) => {
    if (!bearing.relativeGeometry) {
      console.error('Bearing does not have relative geometry.');
      return;
    }

    const componentDesign = getState().componentDesigns.data[componentDesignGuid];
    if (!componentDesign) throw new Error('Component design not found');
    const bearingBase: RelativeBearingsGeometryBase = {
      guid: bearing.relativeGeometry.guid,
      geometryType: 'Bearing',
      bearingGeometryType: newBearingGeometryType,
      bearingLineGuid: bearing.relativeGeometry.bearingLineGuid,
      directionReversed: bearing.relativeGeometry.directionReversed,
      bearingName: bearing.relativeGeometry.bearingName,
    };
    let newRelativeGeometry: RelativeBearingGeometry | RelativeContinuousBearingGeometry;
    if (newBearingGeometryType === 'Bearing') {
      // When switching to a regular bearing, try to put the bearing where another bearing isn't already defined.
      // Do this by x positions since bearing definition lines could be in the same place but be different lines.
      // This could behave unexpectedly for vertical bearings, but should behave helpfully in general cases.
      const findAndCalculateLineX = (lineGuid: string | null) => {
        const line = findAndCalculateLine(lineGuid, componentDesign.component);
        return line?.basePoint.x ?? null;
      };
      const existingBearingDefinitionLineXs =
        componentDesign.component.bearings
          .filter((componentBearing) => componentBearing.guid !== bearing.guid)
          .flatMap((bearing) => {
            const bearingRelativeGeometry = bearing.relativeGeometry;
            if (!bearingRelativeGeometry) return [];
            if ('leftSideLineGuid' in bearingRelativeGeometry) {
              return [
                findAndCalculateLineX(bearingRelativeGeometry.leftSideLineGuid),
                findAndCalculateLineX(bearingRelativeGeometry.rightSideLineGuid),
              ];
            }
            return [findAndCalculateLineX(bearingRelativeGeometry.lineGuid)];
          })
          .filter(notNullish) ?? [];
      const formerBearingRelativeGeometry = bearing.relativeGeometry;
      if (!('leftSideLineGuid' in formerBearingRelativeGeometry)) {
        throw new Error('Bearing should logically be continuous');
      }
      const formerBearingLeftSideLineX = findAndCalculateLineX(formerBearingRelativeGeometry.leftSideLineGuid);
      const leftSideLinePositionUsed =
        formerBearingLeftSideLineX !== null && existingBearingDefinitionLineXs.includes(formerBearingLeftSideLineX);
      const formerBearingRightSideLineX = findAndCalculateLineX(formerBearingRelativeGeometry.rightSideLineGuid);
      const rightSideLinePositionUsed =
        formerBearingRightSideLineX !== null && existingBearingDefinitionLineXs.includes(formerBearingRightSideLineX);
      let lineGuid: string;
      let lineLocation: LineLocation;
      if (!leftSideLinePositionUsed) {
        lineGuid = formerBearingRelativeGeometry.leftSideLineGuid;
        lineLocation = 'Starboard';
      } else if (!rightSideLinePositionUsed) {
        lineGuid = formerBearingRelativeGeometry.rightSideLineGuid;
        lineLocation = 'Port';
      } else {
        lineGuid = formerBearingRelativeGeometry.leftSideLineGuid;
        lineLocation = 'Center';
      }
      const relativeBearingGeometry: RelativeBearingGeometry = {
        ...bearingBase,
        bearingGeometryType: 'Bearing',
        width: 3.5,
        lineGuid,
        lineLocation,
      };
      newRelativeGeometry = relativeBearingGeometry;
    } else {
      const explicitReferenceLines = componentDesign.component.referenceLines?.filter(isExplicitCalculatedLine);
      // This is weak linking to the reference lines, but makes this easier for the user and less likely to break things
      // than the alternative of starting the reference lines as null.
      const verticalZeroReferenceLine = explicitReferenceLines?.find(
        (line) => line.guid === componentDesign.component.verticalZeroGuid,
      );
      const verticalMaxReferenceLine = explicitReferenceLines?.find(
        (line) => line.guid === componentDesign.component.verticalMaxGuid,
      );
      const horizontalZeroReferenceLine = explicitReferenceLines?.find(
        (line) => line.guid === componentDesign.component.horizontalZeroGuid,
      );

      // Fallback to an arbitrary reference line if the standard reference lines can't be found.
      // The standard reference lines should always exist, so we're not too concerned about which reference lines we
      // choose as fallbacks, so we just choose the first one for all of them.
      const fallbackReferenceLine = componentDesign?.component.referenceLines?.at(0);
      if (!fallbackReferenceLine) {
        dispatch(enqueueError('No reference lines exist on component.'));
        return;
      }

      const bearingLine = horizontalZeroReferenceLine ?? fallbackReferenceLine;
      const leftSideLine = verticalZeroReferenceLine ?? fallbackReferenceLine;
      const rightSideLine = verticalMaxReferenceLine ?? fallbackReferenceLine;

      const relativeBearingGeometry: RelativeContinuousBearingGeometry = {
        ...bearingBase,
        bearingGeometryType: 'ContinuousBearing',
        bearingLineGuid: bearingLine.guid,
        leftSideLineGuid: leftSideLine.guid,
        rightSideLineGuid: rightSideLine.guid,
      };
      newRelativeGeometry = relativeBearingGeometry;
    }

    const newBearing = { ...bearing, relativeGeometry: newRelativeGeometry };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export const finishSetBearingLine =
  (newLineGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    const pickingObjectInfo = getPickingObjectInfo(state);

    if (
      pickingObjectInfo == null ||
      pickingObjectInfo.type !== 'pickingReferenceLine' ||
      pickingObjectInfo.targetInfo.type !== 'bearingLine'
    ) {
      throw new Error();
    }

    const { targetInfo } = pickingObjectInfo;
    const activeComponentDesign = getActiveComponentDesign(state)!;
    const bearing = activeComponentDesign.component.bearings.find(
      (possibleMember) => possibleMember.guid === targetInfo.bearingGuid,
    );
    if (bearing && targetInfo.bearingGeometryType === 'Bearing') {
      dispatch(setBearingLineRegularBearing(bearing, activeComponentDesign.guid, targetInfo.propertyName, newLineGuid));
    } else if (bearing && targetInfo.bearingGeometryType === 'ContinuousBearing') {
      dispatch(
        setBearingLineContinuousBearing(bearing, activeComponentDesign.guid, targetInfo.propertyName, newLineGuid),
      );
    } else {
      console.error('Expected target info to be bearing.');
    }

    dispatch(clearPickingObject());
  };

export const changeBearingName =
  (bearing: Bearing, componentDesignGuid: string, newName: string): ThunkActionVoid =>
  (dispatch) => {
    const newBearing = { ...bearing, name: newName };
    dispatch(setBearingOnComponentDesign(newBearing, componentDesignGuid));
  };

export function getDefaultConnectorForType(guid: string, connectorType: ConnectorType): Connector {
  switch (connectorType) {
    case 'Special':
      return { guid, connectorType: 'Special' };
    case 'Hanger':
      return { guid, connectorType: 'Hanger', configurationGuid: '0fee46b9-52e4-4ce8-9493-b68436fc05f2' }; // LUS28
    default:
      assertExhaustiveSwitch(connectorType, 'Unrecognized connector type.');
  }
}

export interface HasConnectors {
  connectors: readonly Connector[];
}

export const addBearingConnector = <B extends HasConnectors>(bearing: B): B => ({
  ...bearing,
  connectors: [...bearing.connectors, getDefaultConnectorForType(crypto.randomUUID(), 'Special')],
});

export const updateBearingConnector = <B extends HasConnectors>(bearing: B, connector: Connector): B => ({
  ...bearing,
  connectors: pushUniqByGuid(bearing.connectors, connector),
});

export const removeBearingConnector = <B extends HasConnectors>(bearing: B, connectorGuid: string): B => ({
  ...bearing,
  connectors: bearing.connectors.filter((connector) => connector.guid !== connectorGuid),
});

export const addConnectorToBearing =
  (bearing: Bearing, componentDesignGuid: string): ThunkActionVoid =>
  (dispatch) =>
    dispatch(setBearingOnComponentDesign(addBearingConnector(bearing), componentDesignGuid));

export const updateConnectorOnBearing =
  (bearing: Bearing, componentDesignGuid: string, connector: Connector): ThunkActionVoid =>
  (dispatch) =>
    dispatch(setBearingOnComponentDesign(updateBearingConnector(bearing, connector), componentDesignGuid));

export const removeConnectorFromBearing =
  (bearing: Bearing, componentDesignGuid: string, connectorGuid: string): ThunkActionVoid =>
  (dispatch) =>
    dispatch(setBearingOnComponentDesign(removeBearingConnector(bearing, connectorGuid), componentDesignGuid));
